import { useCallback, useEffect, useState } from "react";
import adminApi from "../api/adminApi";
import { createPhotosessionStyle } from "../utils/defaults";
import {
  moveArrayItem,
  prependArrayItem,
  removeArrayItem,
  updateArrayItem
} from "../utils/arrayEditors";

function getDefaultPhotoAnimationConfig() {
  return {
    pageSize: 5,
    tokenCost: 12000,
    durationSec: 6,
    resolution: "480p",
    aspectRatio: "16:9",
    oneTimeProductId: 18,
    packProductId: 19,
    styles: []
  };
}

export function useAdminPhotoAnimation({ adminToken, enabled }) {
  const [photoAnimationConfig, setPhotoAnimationConfig] = useState(null);
  const [photoAnimationLoading, setPhotoAnimationLoading] = useState(false);
  const [photoAnimationSaving, setPhotoAnimationSaving] = useState(false);
  const [photoAnimationDirty, setPhotoAnimationDirty] = useState(false);
  const [photoAnimationUploadingIndex, setPhotoAnimationUploadingIndex] = useState(null);

  const load = useCallback(async (token = adminToken) => {
    if (!token) return;
    setPhotoAnimationLoading(true);
    try {
      const res = await adminApi.getPhotoAnimationConfig(token);
      setPhotoAnimationConfig(res.data || null);
      setPhotoAnimationDirty(false);
    } catch (error) {
      console.error("Ошибка загрузки оживления фото:", error);
    } finally {
      setPhotoAnimationLoading(false);
    }
  }, [adminToken]);

  const save = useCallback(async () => {
    if (!adminToken || !photoAnimationConfig) return;
    setPhotoAnimationSaving(true);
    try {
      const res = await adminApi.savePhotoAnimationConfig(adminToken, photoAnimationConfig);
      if (res.data?.config) {
        setPhotoAnimationConfig(res.data.config);
      }
      setPhotoAnimationDirty(false);
    } catch (error) {
      console.error("Ошибка сохранения оживления фото:", error);
      window.alert("Ошибка сохранения оживления фото");
    } finally {
      setPhotoAnimationSaving(false);
    }
  }, [adminToken, photoAnimationConfig]);

  useEffect(() => {
    if (!enabled || !adminToken) return;
    load(adminToken);
  }, [enabled, adminToken, load]);

  const updateConfig = useCallback((patch) => {
    setPhotoAnimationConfig((prev) => (prev ? { ...prev, ...patch } : prev));
    setPhotoAnimationDirty(true);
  }, []);

  const mutateStyles = useCallback((updater) => {
    setPhotoAnimationConfig((prev) => {
      const base = prev || getDefaultPhotoAnimationConfig();
      const styles = Array.isArray(base.styles) ? base.styles : [];
      return {
        ...base,
        styles: updater(styles)
      };
    });
    setPhotoAnimationDirty(true);
  }, []);

  const uploadPreview = useCallback(async (styleIndex, file) => {
    if (!adminToken || !file) return;
    setPhotoAnimationUploadingIndex(styleIndex);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res = await adminApi.uploadPhotoAnimationPreview(adminToken, formData);
      const attachment = String(res.data?.attachment || "").trim();
      if (!attachment) {
        throw new Error("empty attachment");
      }
      mutateStyles((styles) => updateArrayItem(styles, styleIndex, { previewAttachment: attachment }));
    } catch (error) {
      console.error("Ошибка загрузки превью оживления фото:", error);
      const status = Number(error?.response?.status || 0);
      if (status === 413) {
        window.alert("Файл слишком большой. Максимум 20 МБ.");
      } else {
        const serverError = String(error?.response?.data?.error || "").trim();
        window.alert(serverError || "Ошибка загрузки превью (фото/GIF)");
      }
    } finally {
      setPhotoAnimationUploadingIndex(null);
    }
  }, [adminToken, mutateStyles]);

  const styles = Array.isArray(photoAnimationConfig?.styles) ? photoAnimationConfig.styles : [];

  return {
    state: {
      photoAnimationConfig,
      photoAnimationLoading,
      photoAnimationSaving,
      photoAnimationDirty,
      photoAnimationUploadingIndex,
      styles
    },
    actions: {
      reload: () => load(adminToken),
      save,
      updateConfig,
      updateStyle: (index, patch) => mutateStyles((list) => updateArrayItem(list, index, patch)),
      moveStyle: (index, direction) => mutateStyles((list) => moveArrayItem(list, index, direction)),
      removeStyle: (index) => mutateStyles((list) => removeArrayItem(list, index)),
      addStyle: () => mutateStyles((list) => prependArrayItem(list, createPhotosessionStyle())),
      uploadPreview
    }
  };
}

export default useAdminPhotoAnimation;
